import productImages from '~/js/components/product-images';
import variantInfoUpdater from '~/js/components/variant-info-updater';
import deliveryEstimate from '~/js/components/delivery-estimate';
import stickyAddToCart from '~/js/components/sticky-add-to-cart';

let productPage;

// A product section is wired up at most once.
const initializedPages = new WeakSet();

// Labels are literals, not `init.name` — minification mangles those to a single letter.
const safeInit = (label, init) => {
  try {
    init();
  } catch (error) {
    console.error(
      `[product-page] ${label} failed to init; skipping it.`,
      error
    );
  }
};

const initProductImages = () => {
  const productImagesComponent = productPage.querySelector(
    '[data-component="productImages"]'
  );

  if (productImagesComponent) productImages(productImagesComponent);
};

const initVariantInfoUpdater = () => {
  const variantInfoComponent = productPage.querySelector(
    '[data-component="variantInfo"]'
  );

  if (variantInfoComponent) variantInfoUpdater(variantInfoComponent);
};

const initBackButton = () => {
  const backButton = productPage.querySelector('[data-back-button]');
  if (backButton)
    backButton.addEventListener('click', () => {
      window.history.back();
    });
};

const initProductRating = () => {
  const productRating = productPage.querySelector('[data-product-rating]');
  // The trustedshops global is absent whenever its script is blocked or fails — an
  // expected outcome, so bail quietly rather than let safeInit log it as a failure.
  if (
    productRating &&
    location.hostname === 'fermliving.com' &&
    typeof productStickerSummary !== 'undefined'
  ) {
    var summaryBadge = new productStickerSummary();
    summaryBadge.showSummary({
      tsId: 'XF575F4570D00FB0E628016AC1413C06E',
      sku: [productRating.dataset.sku],
      element: '[data-product-rating]',
      starColor: '#000',
      starSize: '14px',
      fontSize: '12px',
      showRating: 'true',
      scrollToReviews: 'false',
      enablePlaceholder: 'true',
    });
  }
};

const initStoreInventoryButton = () => {
  const storeInventoryButton = productPage.querySelector(
    '[data-store-inventory-button]'
  );
  const storeInventoryDropdown = productPage.querySelector(
    '[data-store-inventory-dropdown]'
  );

  if (!storeInventoryButton || !storeInventoryDropdown) {
    return;
  }

  const textEl = storeInventoryDropdown.querySelector(
    '[data-store-inventory-text]'
  );
  const dotEl = storeInventoryDropdown.querySelector(
    '[data-store-inventory-dot]'
  );
  const storeName = storeInventoryDropdown.dataset.storeName || '';
  const inStockText = storeInventoryDropdown.dataset.inStockText || '';
  const outOfStockText = storeInventoryDropdown.dataset.outOfStockText || '';
  const loadingText = storeInventoryDropdown.dataset.loadingText || 'Fetching store inventory';

  const availabilityCache = {};

  const updateDropdown = (available) => {
    if (dotEl) {
      dotEl.className = `border-0.5 mr-1 block h-[7px] w-[7px] rounded-full border-black/25 ${available ? 'bg-green' : 'bg-black'}`;
    }
    if (textEl) {
      textEl.textContent = `${storeName} - ${available ? inStockText : outOfStockText}`;
    }
  };

  const getSelectedVariantId = () => {
    // 1. Try the variant select dropdown
    const variantSelect = productPage.querySelector('[data-variant-select]');
    if (variantSelect && variantSelect.value && variantSelect.value !== 'first_option') {
      return variantSelect.value;
    }

    // 2. Try the add-to-cart component's data attribute
    const addToCart = productPage.querySelector('[data-variant-id]');
    if (addToCart?.dataset.variantId) {
      return addToCart.dataset.variantId;
    }

    // 3. Try the URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('variant');
  };

  // Prefetch availability in the background (no UI update)
  const prefetchAvailability = (variantId) => {
    if (!variantId || availabilityCache[variantId] !== undefined) return;

    const url = `https://fermliving-express-variant-fetcher.vercel.app/inventory-at-location/${variantId}?location_id=35364208704`;

    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        availabilityCache[variantId] = data.available ?? false;
      })
      .catch(() => {});
  };

  const showAvailability = () => {
    const variantId = getSelectedVariantId();
    if (!variantId) return;

    if (availabilityCache[variantId] !== undefined) {
      updateDropdown(availabilityCache[variantId]);
      storeInventoryDropdown.classList.remove('hidden');
    } else {
      // Prefetch hasn't completed yet — show loading with animated dots
      let dotCount = 0;
      if (dotEl)
        dotEl.className =
          'border-0.5 mr-1 block h-[7px] w-[7px] rounded-full border-black/25 bg-black/25';
      storeInventoryDropdown.classList.remove('hidden');

      const dotInterval = setInterval(() => {
        dotCount = (dotCount + 1) % 4;
        if (textEl) textEl.textContent = `${loadingText}${'.'.repeat(dotCount || 1)}`;
      }, 200);
      if (textEl) textEl.textContent = `${loadingText}.`;

      const checkInterval = setInterval(() => {
        if (availabilityCache[variantId] !== undefined) {
          clearInterval(checkInterval);
          clearInterval(dotInterval);
          updateDropdown(availabilityCache[variantId]);
        }
      }, 100);

      // Stop checking after 5 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        clearInterval(dotInterval);
      }, 5000);
    }
  };

  storeInventoryButton.addEventListener('click', () => {
    if (!storeInventoryDropdown.classList.contains('hidden')) {
      storeInventoryDropdown.classList.add('hidden');
      return;
    }
    showAvailability();
  });

  // Prefetch on variant change and hide dropdown
  document.addEventListener('variant:change', (event) => {
    storeInventoryDropdown.classList.add('hidden');
    const { variant } = event.detail;
    if (variant?.id) {
      prefetchAvailability(String(variant.id));
    }
  });

  // Prefetch for the initial variant once the browser is idle
  const idleCallback = window.requestIdleCallback || ((cb) => setTimeout(cb, 2000));
  idleCallback(() => prefetchAvailability(getSelectedVariantId()));
};

const initDeliveryEstimate = () => {
  const el = productPage.querySelector('[data-component="deliveryEstimate"]');
  if (el) deliveryEstimate(el);
};

const initStickyAddToCart = () => {
  const el = productPage.querySelector('[data-sticky-atc]');
  if (el) stickyAddToCart(el);
};

const initProductPage = () => {
  productPage = document.querySelector('[data-component="productPage"]');
  if (!productPage) return;

  // Same node as a previous run means the section was not re-rendered and the inits below
  // have already run. A genuine section reload replaces this node, so it re-initialises.
  if (initializedPages.has(productPage)) return;
  initializedPages.add(productPage);

  // Isolated individually: initProductRating dereferences a third-party global that is not
  // always present, and a throw must not take the rest of the buy box down with it.
  safeInit('product images', initProductImages);
  safeInit('variant info updater', initVariantInfoUpdater);
  safeInit('back button', initBackButton);
  safeInit('product rating', initProductRating);
  safeInit('store inventory button', initStoreInventoryButton);
  safeInit('delivery estimate', initDeliveryEstimate);
  safeInit('sticky add to cart', initStickyAddToCart);
};

document.addEventListener('DOMContentLoaded', initProductPage);
// Unscoped on purpose: initProductPage re-queries and lets initializedPages decide whether
// there is anything new to wire up.
document.addEventListener('shopify:section:load', initProductPage);
